import { ChatMessage, ActionType } from "../../types";
import { HostType } from "../../types/office";
import { API_BASE_URL } from "../../../config";

/**
 * API Base URL Configuration
 * 
 * IMPORTANT: The FastAPI backend must have CORS configured to allow requests from:
 * - Development: https://localhost:3000
 * - Production: Your production frontend origin
 * 
 * Example FastAPI CORS configuration (add to your backend main.py or app initialization):
 * ```python
 * from fastapi.middleware.cors import CORSMiddleware
 * 
 * app.add_middleware(
 *     CORSMiddleware,
 *     allow_origins=["https://localhost:3000"],  # Frontend origin
 *     allow_credentials=True,  # Required for Authorization header
 *     allow_methods=["*"],  # Or specify: ["GET", "POST", "OPTIONS"]
 *     allow_headers=["*"],  # Or specify: ["Content-Type", "Authorization"]
 * )
 * ```
 * 
 * If you're still having CORS issues, also ensure:
 * 1. The backend is running and accessible
 * 2. The backend port matches (default FastAPI port is 8000, adjust if different)
 * 3. Try using "http://localhost:8000/api/v1" instead of "http://127.0.0.1/api/v1"
 * 
 * The API_BASE_URL is now configured in config.dev.ts (dev) and config.prod.ts (prod)
 */

/**
 * Auth-related types and interfaces
 */
export interface UserInfo {
  oid: string;
  name: string;
  upn: string;
  tenant: string;
}

export interface LicenseInfo {
  id: string;
  name: string;
  active: boolean;
}

export interface AvailableModel {
  provider: string;
  id: string;
  name: string;
}

export interface KnowledgeBase {
  id: string;
  name: string;
}

export interface UserInfoResponse {
  user: UserInfo;
  license: LicenseInfo;
  available_models: AvailableModel[];
  knowledge_bases: KnowledgeBase[];
}

/**
 * Gets user information from the backend using SSO token
 * @param token - The SSO access token (should be retrieved from getUserIdentity())
 * @returns Promise<UserInfoResponse> - Contains user info, license info, available models, and knowledge bases
 * @throws Error if authentication fails or user is not authorized
 */
export async function getUserInfo(token: string): Promise<UserInfoResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/user-info`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Authentication failed: ${response.status} ${response.statusText}. ${errorText}`);
    }

    const data: UserInfoResponse = await response.json();
    
    // Note: We no longer throw if license is inactive - let the caller handle it
    // This allows the UI to load but show a license warning message

    return data;
  } catch (error) {
    console.error('Authentication error:', error);
    throw error;
  }
}


/**
 * Maps ActionType to quick action string for Outlook, Word, and PowerPoint
 */
function getQuickAction(actionType: ActionType, host: HostType): string | undefined {
  if (host === "Outlook") {
    switch (actionType) {
      case "summarize":
        return "outlook.summarize";
      case "grammar":
        return "outlook.grammar";
      case "actionItems":
        return "outlook.actionitems";
      case "replyOptions":
        return "outlook.reply";
      case "review":
        return "outlook.template";
      case "professional":
        return "outlook.polish";
      default:
        return undefined;
    }
  }

  if (host === "Word") {
    switch (actionType) {
      case "summarize":
        return "word.summarize";
      case "grammar":
        return "word.grammar";
      default:
        return undefined;
    }
  }

  if (host === "PowerPoint") {
    switch (actionType) {
      case "summarize":
        return "powerpoint.summarize";
      case "notes":
        return "powerpoint.notes";
      case "rewrite":
        return "powerpoint.rewrite";
      default:
        return undefined;
    }
  }

  if (host === "Excel") {
    switch (actionType) {
      case "summarize":
        return "excel.summarize";
      case "pivottable":
        return "excel.pivottable";
      default:
        return undefined;
    }
  }

  return undefined;
}

/**
 * Converts model ID to backend-compatible format
 */
function normalizeModel(model: string): string {
  // Backend expects exact model names, so return as-is
  return model;
}

/**
 * Streams LLM responses from FastAPI backend for chat
 */
export async function streamMessage(
  token: string, // Access token from MSAL (should be retrieved from getUserIdentity())
  systemPrompt: string, // System prompt will be added to context, not messages
  chat: ChatMessage[], // Chat history in OpenRouter format
  userContent: string,
  onChunkReceived: (chunk: string) => void,
  onComplete: () => void,
  signal?: AbortSignal,
  model: string = "gpt-4o-mini",
  host: HostType = "Unknown",
  actionType?: ActionType,
  context?: Record<string, any>,
  knowledgeBase?: KnowledgeBase | null
) {
  try {

    // Get quick action if applicable
    const quickAction = actionType ? getQuickAction(actionType, host) : undefined;

    // Build messages array - only chat history and user message (no system prompt)
    // System prompt and other context go in the context object
    const chatMessages = chat.map(msg => ({
      content: msg.content,
      role: msg.role
    }));
    const userMessage = { role: "user", content: userContent };
    
    const messages = [...chatMessages, userMessage];

    // Build request body according to API spec
    const requestBody: any = {
      model: normalizeModel(model),
      application: host === "Unknown" ? "Outlook" : host, // Default to Outlook if unknown
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      messages: messages, // Only chat history and current user message
    };

    // Build context object - include system prompt and any additional context
    const contextObject: Record<string, any> = {
      ...context,
    };
    
    // Add system prompt to context if provided (backend will combine it)
    if (systemPrompt) {
      contextObject.system_prompt = systemPrompt;
    }

    // Add context to request body
    if (Object.keys(contextObject).length > 0) {
      requestBody.context = contextObject;
    }

    // Add quick action if available
    if (quickAction) {
      requestBody.quickaction = quickAction;
    }

    // Add knowledge base if available
    if (knowledgeBase) {
      requestBody.knowledgebase = {
        id: knowledgeBase.id,
        name: knowledgeBase.name
      };
    }

    const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
      
      // Check for 402 status code with insufficient tokens error
      if (response.status === 402) {
        const errorDetail = error.detail || error;
        const errorMessage = typeof errorDetail === 'string' ? errorDetail : errorDetail?.error || errorDetail?.message;
        
        // Check if error message contains "Insufficient tokens" or similar
        if (errorMessage && (errorMessage.includes("Insufficient tokens") || errorMessage.includes("insufficient tokens"))) {
          const insufficientTokensError: any = new Error("INSUFFICIENT_TOKENS");
          insufficientTokensError.isInsufficientTokens = true;
          insufficientTokensError.statusCode = 402;
          insufficientTokensError.originalError = errorDetail;
          throw insufficientTokensError;
        }
      }
      
      throw new Error(error.detail || `API error: ${response.status}`);
    }

    // Backend streams plain text tokens, not SSE format
    const reader = response.body?.getReader();
    if (!reader) throw new Error("Stream reader not available");

    const decoder = new TextDecoder();
    let accumulatedText = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      accumulatedText += chunk;
      onChunkReceived(accumulatedText);
    }

    onComplete();
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error("Error streaming from FastAPI backend:", error);
      
      // Check if this is an insufficient tokens error
      if (error.isInsufficientTokens || error.message === "INSUFFICIENT_TOKENS") {
        onChunkReceived("Insufficient tokens. Please contact support.");
      } else {
        onChunkReceived("Sorry, there was an error connecting to the AI service.");
      }
    }
    onComplete();
  }
}

/**
 * Formats content for Excel insertion using FastAPI backend
 * Uses Excel-specific API endpoint with selectedCell and sheetName
 */
export async function formatExcelContent(
  token: string, // Access token from MSAL (should be retrieved from getUserIdentity())
  text: string,
  selectedCell: string,
  sheetName: string,
  model: string = "gpt-4o-mini",
  range?: string
): Promise<string> {
  try {
    const requestBody: any = {
      text,
      selectedCell,
      sheetName,
      model: normalizeModel(model),
    };

    // Add context object with range and activeWorksheet
    const context: Record<string, any> = {};
    if (range) {
      context.range = range;
    }
    if (sheetName) {
      context.activeWorksheet = sheetName;
    }
    
    if (Object.keys(context).length > 0) {
      requestBody.context = context;
    }

    const response = await fetch(`${API_BASE_URL}/insert/excel`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
      throw new Error(error.detail || `API error: ${response.status}`);
    }

    // Stream and accumulate formatted content
    const reader = response.body?.getReader();
    if (!reader) throw new Error("Stream reader not available");

    const decoder = new TextDecoder();
    let formattedContent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      formattedContent += chunk;
    }

    return formattedContent;
  } catch (error) {
    console.error("Error formatting Excel content:", error);
    throw error;
  }
}

/**
 * Formats content for insertion into Office documents using FastAPI backend
 */
export async function formatContent(
  token: string, // Access token from MSAL (should be retrieved from getUserIdentity())
  text: string,
  application: HostType,
  context?: string,
  model: string = "gpt-4o-mini"
): Promise<string> {
  try {

    const requestBody: any = {
      text,
      application: application === "Unknown" ? "Outlook" : application,
      model: normalizeModel(model),
    };

    if (context) {
      requestBody.context = context;
    }

    const response = await fetch(`${API_BASE_URL}/insert/format`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
      throw new Error(error.detail || `API error: ${response.status}`);
    }

    // Stream and accumulate formatted content
    const reader = response.body?.getReader();
    if (!reader) throw new Error("Stream reader not available");

    const decoder = new TextDecoder();
    let formattedContent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      formattedContent += chunk;
    }

    // Parse JSON response and extract content field
    try {
      const parsed = JSON.parse(formattedContent);
      return parsed.content || formattedContent; // Fallback to original if content field doesn't exist
    } catch (e) {
      // If parsing fails, return the original content (backward compatibility)
      return formattedContent;
    }
  } catch (error) {
    console.error("Error formatting content:", error);
    throw error;
  }
}

